import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../../component/context/AuthContext";
import * as CONST from "../../../component/config/Constant";
import { toast } from "react-toastify";
import Joi from "joi";
import "../../../assets/css/table.css";
import AddQuestionModel from "./AddQuestionModel";
import Swal from "sweetalert2";
import ViewSetModal from "./ViewSetModal";
import HeaderSection from "../banner/HeaderSection";
import Loading from "react-fullscreen-loading";
import { EditIcon, DeleteIcon, ViewIcon } from '../../../component/utilities/ActionIcons';

export default function SetForm() {
  const [authUserData] = useContext(AuthContext);
  const token = authUserData && authUserData[0] ? authUserData[0].token : "";
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [fullLoading, setFullLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [timeLimit, setTimeLimit] = useState("");
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [viewQuestionId, setViewQuestionId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // 1. Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchAllQuestions();
    if (id) fetchSet();
  }, [id]);

  // Reset to page 1 whenever user searches
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredQuestions = useMemo(() => {
    return allQuestions
      .filter((q) =>
        q.question_text?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .sort((a, b) => {
        const aId = String(a.id || a.question_id);
        const bId = String(b.id || b.question_id);
        const aChecked = selectedQuestionIds.some((id) => String(id) === aId);
        const bChecked = selectedQuestionIds.some((id) => String(id) === bId);
        if (aChecked === bChecked) return 0;
        return aChecked ? -1 : 1;
      });
  }, [allQuestions, searchTerm, selectedQuestionIds]);

  // 2. Logic for Pagination
  const totalPages = Math.ceil(filteredQuestions.length / itemsPerPage);
  const paginatedQuestions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredQuestions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredQuestions, currentPage]);

  const schema = Joi.object({
    title: Joi.string().required().messages({ "string.empty": "Title is required" }),
    description: Joi.string().required().messages({ "string.empty": "Description is required" }),
    timeLimit: Joi.number().integer().min(1).max(120).required().messages({
      "number.base": "Time limit must be a number",
      "number.min": "Time limit must be at least 1 minute",
      "number.max": "Time limit must be less than 120 minute",
      "any.required": "Time limit is required",
    }),
    question_ids: Joi.array().min(1).items(Joi.number(), Joi.string()).required().messages({
      "array.min": "Please select at least one question",
    }),
  });

  async function fetchAllQuestions(newQuestionId = null) {
    try {
      const resp = await axios.get(CONST.ISO_LIST_QUESTIONS, {
        headers: { Authorization: `Basic ${token}` },
        params: { page: 1, limit: 1000 },
      });

      if (resp.data && resp.data.status === "success") {
        const qData = resp.data.data.questions || resp.data.data || [];
        const questionsList = Array.isArray(qData) ? qData : [];
        setAllQuestions(questionsList);

        if (newQuestionId) {
          const sid = String(newQuestionId);
          setSelectedQuestionIds((prev) => {
            const existingIds = prev.map(id => String(id));
            if (existingIds.includes(sid)) return existingIds;
            return [...existingIds, sid];
          });
          setSearchTerm("");
          setCurrentPage(1);
        }
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error("Failed to load questions");
    }
  }

  const handleQuestionAdded = (newQuestion) => {
    const target = Array.isArray(newQuestion) ? newQuestion[0] : newQuestion;
    const qid = (target && typeof target === 'object') ? (target.id || target.question_id) : target;
    if (qid) fetchAllQuestions(qid);
    else fetchAllQuestions();
  };

  async function fetchSet() {
    setLoading(true);
    try {
      const resp = await axios.get(CONST.ISO_SET_GET + id, {
        headers: { Authorization: `Basic ${token}` },
      });
      if (resp.data && resp.data.status === "success") {
        const d = resp.data.data;
        setTitle(d.title || "");
        setDescription(d.description || "");
        setTimeLimit(d.time_limit_minutes || "");
        setIsActive(d.is_active !== undefined ? d.is_active : true);
        const qids = (d.questions || d.question_details || []).map((q) => String(q.id || q.question_id));
        setSelectedQuestionIds(qids);
      }
    } catch (err) {
      toast.error("Error fetching set");
    }
    setLoading(false);
  }

  function toggleQuestion(qid) {
    const sid = String(qid);
    setSelectedQuestionIds((prev) => {
      const stringIds = prev.map(id => String(id));
      if (stringIds.includes(sid)) return stringIds.filter((i) => i !== sid);
      return [...stringIds, sid];
    });
  }

  const handleTimeChange = (e) => {
    const value = e.target.value;
    if (value === "" || !isNaN(Number(value))) setTimeLimit(value);
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const formData = { title, description, timeLimit, question_ids: selectedQuestionIds };
    const { error } = schema.validate(formData, { abortEarly: true });
    if (error) {
      toast.error(error.details[0].message);
      return;
    }

    setFullLoading(true);
    try {
      const payload = {
        title,
        description,
        time_limit_minutes: Number(timeLimit),
        question_ids: selectedQuestionIds,
        is_active: isActive,
      };
      const config = { headers: { Authorization: `Basic ${token}` } };
      const resp = id
        ? await axios.put(CONST.ISO_SET_UPDATE + id, payload, config)
        : await axios.post(CONST.ISO_SET_CREATE, payload, config);

      if (resp.data && resp.data.status === "success") {
        toast.success(id ? "Updated" : "Added");
        navigate(CONST.ADMIN_PRE + "iso-quiz/sets");
      } else {
        toast.error(resp.data.message || "Save failed");
      }
    } catch (err) {
      toast.error("Save failed");
    }
    setFullLoading(false);
  }

  async function handleDeleteQuestion(questionId) {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this question?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it",
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });
    if (!result.isConfirmed) return;

    try {
      const question = allQuestions.find(q => String(q.id || q.question_id) === String(questionId));
      const payload = {
        ...question,
        status: 'inactive',
        updated_by: authUserData && authUserData[0] ? authUserData[0].user_id : ''
      };
      const resp = await axios.put(CONST.ISO_UPDATE_QUESTION + questionId, payload, {
        headers: { Authorization: `Basic ${token}` },
      });
      if (resp.data?.status === "success") {
        toast.success("Question deleted successfully");
        setAllQuestions((prev) => prev.filter((q) => String(q.id || q.question_id) !== String(questionId)));
        setSelectedQuestionIds((prev) => prev.filter((id) => String(id) !== String(questionId)));
      } else {
        toast.error(resp.data?.message || "Deletion failed");
      }
    } catch (err) {
      toast.error("Status update API error");
    }
  }

  const handleViewQuestions = (questionId) => {
    setViewQuestionId(questionId);
    setShowViewModal(true);
  };

  return (
    <>
      <Loading loading={fullLoading || loading} background="rgba(0, 0, 0, 0.5)" loaderColor="#fff" />
      <HeaderSection
        title="Admin"
        heading={id ? "Edit Assignment" : "Add Assignment"}
        addLinkName="Add Question"
        addLink="#"
        addButton={() => setShowQuestionModal(true)}
        linktype="add"
      />
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-4">
                  <div className="col-md-9">
                    <label className="form-label required">Title</label>
                    <input
                      className="form-control"
                      placeholder="Enter Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label required">Time Limit (Mins)</label>
                    <input
                      className="form-control"
                      placeholder="Numbers only"
                      value={timeLimit}
                      onChange={handleTimeChange}
                      required
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label required">Description</label>
                    <textarea
                      className="form-control"
                      placeholder="Enter Description"
                      rows="3"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      required
                    />
                  </div>
                  {id && (
                    <div className="col-12 mt-2 d-flex justify-content-end">
                      <label className="form-check form-switch m-0 d-flex align-items-center gap-2">
                        
                        <span className="form-check-label fw-bold m-0 mr-2">
                          Active Status
                        </span>

                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                        />
                        
                      </label>
                    </div>
                  )}
                </div>

                <div className="card border shadow-none">
                  <div className="card-header bg-light d-flex justify-content-between align-items-center">
                    <h3 className="card-title">Select Questions ({selectedQuestionIds.length} Selected)</h3>
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search questions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="card-body p-0">
                    <div className="list-group list-group-flush" style={{ minHeight: "300px", maxHeight: "500px", overflowY: "auto" }}>
                      {paginatedQuestions.map((q) => {
                        const qid = String(q.id || q.question_id);
                        const isChecked = selectedQuestionIds.some((sid) => String(sid) === qid);
                        return (
                          <div key={qid} className="list-group-item d-flex align-items-center justify-content-between py-2">
                            <div className="d-flex align-items-center gap-2">
                              <input
                                className="form-check-input mr-2"
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleQuestion(qid)}
                                id={"q_" + qid}
                              />
                              <label className="form-check-label ms-2 cursor-pointer" htmlFor={"q_" + qid}>
                                {q.question_text}
                              </label>
                            </div>
                            <div className="d-flex gap-2">
                              <ViewIcon onClick={() => handleViewQuestions(qid)} tooltip="View Question" />
                              <EditIcon onClick={() => { setEditingQuestionId(qid); setShowQuestionModal(true); }} tooltip="Edit Question" />
                              <DeleteIcon onClick={() => handleDeleteQuestion(qid)} tooltip="Delete Question" />
                            </div>
                          </div>
                        );
                      })}
                      {paginatedQuestions.length === 0 && (
                        <div className="p-4 text-center text-muted">No questions found.</div>
                      )}
                    </div>
                  </div>
                  {totalPages > 1 && (
                    <div className="card-footer d-flex justify-content-between align-items-center">
                      <span className="text-muted small">Showing page {currentPage} of {totalPages}</span>
                      <ul className="pagination pagination-sm mb-0 ms-auto">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button type="button" className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>«</button>
                        </li>
                        {[...Array(totalPages)].map((_, i) => (
                          <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                            <button type="button" className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                          <button type="button" className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>»</button>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="mt-4 d-flex gap-2">
                  <button className="btn btn-primary px-4 mr-2" disabled={fullLoading}>
                    {id ? "Update Assignment" : "Save Assignment"}
                  </button>
                  <button type="button" className="btn btn-outline-secondary px-4 ml-3" onClick={() => navigate(-1)}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <AddQuestionModel
        show={showQuestionModal}
        handleClose={() => { setShowQuestionModal(false); setEditingQuestionId(null); }}
        editingId={editingQuestionId}
        onQuestionAdded={handleQuestionAdded}
      />

      <ViewSetModal
        show={showViewModal}
        handleClose={() => { setShowViewModal(false); setViewQuestionId(null); }}
        questionId={viewQuestionId}
      />
    </>
  );
}
